/**
 * PDF Relación de Despachos (formato Envía Colvanes / landscape)
 */
import jsPDF from 'jspdf'
import { MANIFIESTO_REMITENTE } from '../../constants/manifiestoRemitente'
import type { ManifiestoCarrierOption, ManifiestoDespachoRow } from '../../types/manifiestos'
import { generateCode128DataUrl } from '../../utils/manifiestos/code128Barcode'
import { buildCitySummary } from '../../utils/manifiestos/manifiestoMapper'

function formatFechaHoy(): string {
  const now = new Date()
  const dd = String(now.getDate()).padStart(2, '0')
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const yyyy = now.getFullYear()
  return `${dd}/${mm}/${yyyy}`
}

function truncate(text: string, max: number): string {
  if (!text) return ''
  return text.length > max ? `${text.slice(0, max - 1)}…` : text
}

function drawHeader(
  doc: jsPDF,
  pageWidth: number,
  margin: number,
  carrierLabel: string
) {
  const r = MANIFIESTO_REMITENTE
  let y = margin
  const title = `RELACIÓN DE DESPACHO - ${carrierLabel}`

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.text(title, pageWidth / 2, y + 14, { align: 'center' })
  doc.setDrawColor(0)
  doc.rect(margin, y, pageWidth - margin * 2, 22)

  y += 28
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  // Ciudad + remitente en un solo bloque (sin línea intermedia)
  doc.text(`CIUDAD: ${r.ciudad}`, margin + 4, y + 10)
  doc.text(`FECHA: ${formatFechaHoy()}`, margin + 220, y + 10)
  doc.text(`REMITE: ${r.remite}`, margin + 4, y + 24)
  doc.text(`CC/NIT: ${r.nit}`, margin + 250, y + 24)
  doc.text(`DIR: ${r.direccion}`, margin + 370, y + 24)
  doc.text(`TEL: ${r.telefono}`, pageWidth - margin - 90, y + 24)
  doc.rect(margin, y, pageWidth - margin * 2, 32)

  return y + 36
}

function drawTableHeader(doc: jsPDF, y: number, cols: number[], margin: number) {
  const labels = [
    '#',
    'DESTINATARIO',
    'CIUDAD',
    'DIRECCION',
    'CONTENIDO',
    'COD.CLIENTE',
    'UND',
    'PESO',
    'VOL',
    'DECL',
    'FLETE',
    'GUIAS',
    'CB',
  ]
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(6.5)
  doc.setFillColor(245, 245, 245)
  doc.rect(margin, y, cols.reduce((a, b) => a + b, 0), 14, 'FD')

  let x = margin
  labels.forEach((label, i) => {
    doc.text(label, x + cols[i] / 2, y + 9, { align: 'center' })
    doc.rect(x, y, cols[i], 14)
    x += cols[i]
  })
  return y + 14
}

/**
 * Genera y descarga el PDF de manifiesto
 */
export async function generarPDFManifiesto(
  carrier: ManifiestoCarrierOption,
  rows: ManifiestoDespachoRow[]
): Promise<{ fileName: string }> {
  if (rows.length === 0) {
    throw new Error('No hay despachos con guía para generar el manifiesto')
  }

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4',
  })

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 28
  const contentWidth = pageWidth - margin * 2
  // Proporciones base; se escalan al ancho del encabezado
  const colWeights = [18, 78, 70, 90, 78, 52, 28, 28, 28, 48, 42, 70, 120]
  const weightSum = colWeights.reduce((a, b) => a + b, 0)
  const cols = colWeights.map((w) => (w / weightSum) * contentWidth)
  const rowHeight = 34
  const tableWidth = contentWidth

  let y = drawHeader(doc, pageWidth, margin, carrier.label)
  y = drawTableHeader(doc, y, cols, margin)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(6)

  let totalUnd = 0
  let totalPeso = 0
  let totalVol = 0
  let totalDecl = 0
  let totalFlete = 0

  for (const row of rows) {
    if (y + rowHeight > pageHeight - 120) {
      doc.addPage()
      y = drawHeader(doc, pageWidth, margin, carrier.label)
      y = drawTableHeader(doc, y, cols, margin)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(6)
    }

    const values = [
      row.numeroVisible || String(row.index),
      truncate(row.destinatario, 28),
      truncate(row.ciudad, 24),
      truncate(row.direccion, 32),
      truncate(row.contenido, 28),
      truncate(row.documento, 14),
      String(row.und),
      String(row.peso),
      String(row.vol),
      row.declLabel,
      row.fleteLabel,
      row.guia,
      '',
    ]

    let x = margin
    values.forEach((value, i) => {
      doc.rect(x, y, cols[i], rowHeight)
      if (i < 12) {
        const align = i >= 6 && i <= 10 ? 'right' : 'left'
        const textX = align === 'right' ? x + cols[i] - 3 : x + 2
        doc.text(value, textX, y + 12, {
          align: align === 'right' ? 'right' : 'left',
          maxWidth: cols[i] - 4,
        })
      }
      x += cols[i]
    })

    try {
      const barcode = generateCode128DataUrl(row.guia, {
        width: 230,
        height: 28,
      })
      if (barcode) {
        const barcodeX = margin + tableWidth - cols[12] + 4
        doc.addImage(barcode, 'PNG', barcodeX, y + 3, cols[12] - 8, 28)
      }
    } catch {
      doc.text(row.guia, margin + tableWidth - cols[12] + 4, y + 18)
    }

    totalUnd += row.und
    totalPeso += row.peso
    totalVol += row.vol
    totalDecl += row.decl
    totalFlete += row.flete
    y += rowHeight
  }

  // Totales
  if (y + 20 > pageHeight - 100) {
    doc.addPage()
    y = margin
  }

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7)
  const totalsLabelWidth = cols.slice(0, 6).reduce((a, b) => a + b, 0)
  doc.rect(margin, y, totalsLabelWidth, 16)
  doc.text('TOTALES', margin + totalsLabelWidth - 6, y + 11, { align: 'right' })

  let tx = margin + totalsLabelWidth
  const totals = [
    String(totalUnd),
    String(totalPeso),
    String(totalVol),
    totalDecl.toLocaleString('en-US'),
    totalFlete.toLocaleString('en-US'),
    String(rows.length),
    '',
  ]
  const totalCols = cols.slice(6)
  totals.forEach((value, i) => {
    doc.rect(tx, y, totalCols[i], 16)
    if (value) {
      doc.text(value, tx + totalCols[i] - 3, y + 11, { align: 'right' })
    }
    tx += totalCols[i]
  })

  y += 28

  // Resumen por centro de distribución
  const summary = buildCitySummary(rows)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
//  doc.text('CENTRO DE DISTRIBUCION', margin + 4, y + 10)

  const sumCols = [120, 50, 50, 60, 60]
  const sumLabels = ['CENTRO DE DISTRIBUCION', 'Items', 'UNDS', 'DOC ENTR', 'DOC DEV']
  y += 14
  let sx = margin
  sumLabels.forEach((label, i) => {
    doc.rect(sx, y, sumCols[i], 14)
    doc.text(label, sx + 3, y + 10)
    sx += sumCols[i]
  })
  y += 14

  doc.setFont('helvetica', 'normal')
  summary.forEach((item) => {
    if (y + 14 > pageHeight - 50) {
      doc.addPage()
      y = margin
    }
    const vals = [
      item.ciudad,
      String(item.items),
      String(item.unds),
      String(item.docEntr),
      String(item.docDev),
    ]
    sx = margin
    vals.forEach((value, i) => {
      doc.rect(sx, y, sumCols[i], 14)
      doc.text(value, sx + (i === 0 ? 3 : sumCols[i] - 3), y + 10, {
        align: i === 0 ? 'left' : 'right',
      })
      sx += sumCols[i]
    })
    y += 14
  })

  y += 36
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.text('EMPLEADO RECIBE', margin, y)
  doc.line(margin, y + 28, margin + 180, y + 28)

  doc.setFontSize(7)
  doc.setTextColor(100)
  doc.text(
    `Transportadora: ${carrier.label}`,
    pageWidth - margin,
    pageHeight - 16,
    { align: 'right' }
  )
  doc.setTextColor(0)

  const stamp = new Date()
    .toISOString()
    .replace(/[-:TZ.]/g, '')
    .slice(0, 14)
  const safeCarrier = carrier.id.replace(/[^a-z0-9-]/gi, '_')
  const fileName = `Manifiesto_${safeCarrier}_${stamp}.pdf`
  doc.save(fileName)

  return { fileName }
}
